/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import * as RadixUIDialog from "@radix-ui/react-dialog";
import type { ComponentProps, ReactNode } from "react";

import type { ThemeType } from "../../../../../global/emotion";
import {
  constantSemanticColors,
  numericPercentages,
  radius,
  spacing,
  useTheme,
  zIndices,
} from "../../../../../global/emotion";
import { cssMediaQueryMinWidthTablet } from "../../../../lib/constants/emotionMediaQueries.constants";
import { IconButton } from "../../_core/IconButton/IconButton.emotion";
import { CloseIcon } from "../../icons/Icon/NavigationIcons";

const contentStyle: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: theme.semanticColors.onOverlayBackground,
  borderRadius: radius.l,
  bottom: spacing.s,
  display: "flex",
  flexDirection: "column",
  left: numericPercentages[50],
  maxWidth: `calc(100vw - ${spacing.s} - ${spacing.s})`,
  overflow: "hidden",
  padding: spacing.m,
  position: "fixed",
  top: "auto",
  transform: "translate(-50%, 0%)",
  width: spacing.full,
  zIndex: zIndices.modalContent,
  [cssMediaQueryMinWidthTablet]: {
    bottom: "auto",
    padding: spacing.s,
    top: numericPercentages[50],
    transform: "translate(-50%, -50%)",
  },
});
const closeStyle: Interpolation = {
  backgroundColor: constantSemanticColors.onOverlayDarkMedLow,
  borderRadius: radius.xxs,
  borderWidth: spacing.none,
  cursor: "pointer",
  display: "block",
  height: spacing.xxs,
  marginBottom: spacing.s,
  marginLeft: "auto",
  marginRight: "auto",
  width: spacing.xl,
  [cssMediaQueryMinWidthTablet]: { display: "none" },
};
const overlayStyle: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: theme.semanticColors.higherContrastMed,
  inset: spacing.none,
  position: "fixed",
  zIndex: zIndices.modalOverlay,
});
const xCloseStyle: Interpolation = {
  position: "absolute",
  right: `calc(${spacing.l} + ${spacing.m})`,
  top: spacing.l,
  zIndex: zIndices[1],
};

export type ModalProps = ComponentProps<typeof RadixUIDialog.Root> & {
  contentProps?: Omit<ComponentProps<typeof RadixUIDialog.Content>, "children">;
  contentCSS?: Interpolation;
  hasMobilePulldownBar?: boolean;
  overlayProps?: ComponentProps<typeof RadixUIDialog.Overlay>;
  overlayCSS?: Interpolation;
  portalProps?: ComponentProps<typeof RadixUIDialog.Portal>;
  trigger?: ReactNode;
  showClose?: boolean;
  closeCSS?: Interpolation;
};

export const Modal = ({
  children,
  closeCSS = {},
  contentProps,
  contentCSS = {},
  hasMobilePulldownBar = false,
  overlayProps,
  overlayCSS = {},
  portalProps,
  trigger,
  showClose,
  ...props
}: ModalProps) => {
  const { themeContainerRef } = useTheme();

  return (
    <RadixUIDialog.Root {...props}>
      <RadixUIDialog.Trigger asChild>{trigger}</RadixUIDialog.Trigger>
      <RadixUIDialog.Portal
        container={themeContainerRef.current}
        {...portalProps}
      >
        <RadixUIDialog.Overlay
          css={[overlayStyle, overlayCSS]}
          {...overlayProps}
        />
        <RadixUIDialog.Content
          css={[contentStyle, contentCSS]}
          {...contentProps}
          onOpenAutoFocus={(event) => event.preventDefault()}
        >
          {hasMobilePulldownBar ? (
            <RadixUIDialog.Close css={[closeStyle, closeCSS]} />
          ) : showClose ? (
            <RadixUIDialog.Close asChild>
              <IconButton
                size={"s"}
                icon={<CloseIcon />}
                variant={"transparentNeutral"}
                css={[xCloseStyle, closeCSS]}
              />
            </RadixUIDialog.Close>
          ) : null}
          {children}
        </RadixUIDialog.Content>
      </RadixUIDialog.Portal>
    </RadixUIDialog.Root>
  );
};
